import { Skeleton, Text, useToast } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { useGetUserActiveCreditFacilitiesQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { errorToastConfig } from 'packages/common-frontend/constants';
import { DeliverableContainers } from 'packages/deliverables-v4/ui/borrower/Deliverables';
import { DeliverableContainerDetailView } from 'packages/deliverables-v4/ui/borrower/DeliverablesDetailView';
import { DeliverableContainerReceipts } from 'packages/deliverables-v4/ui/borrower/DeliverablesReceiptsView';
import { DeliverableDetailRoute } from 'packages/deliverables/ui/DeliverableDetail';
import { DeliverablesTanStack } from 'packages/deliverables/ui/DeliverablesTanStack';
import { getDeliverablesQueryParams } from 'packages/deliverables/ui/utils';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

// Exports
export const DeliverablesRouter = () => {
  const flags = useFeatureFlags();
  const location = useLocation();
  const toast = useToast();
  const queryParams = useMemo(() => {
    return getDeliverablesQueryParams(location.search);
  }, [location.search]);

  const {
    data: userData,
    loading: userLoading,
  } = useGetUserActiveCreditFacilitiesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      creditFacilitySlugsToHide: flags?.creditFacilitySlugsToHide ?? [],
    },
  });

  if (!userData || !userData.user || userLoading) {
    return <Skeleton />;
  }

  const {
    user: {
      id: userId,
      organizationId,
      organization: { creditFacilities, slug: orgSlug },
    },
  } = userData;

  const showV4Deliverables = flags?.showV4Deliverables;

  return (
    // Paths relative to "/deliverables/*"
    <Routes>
      <Route
        path="/"
        element={
          !showV4Deliverables ? (
            <DeliverablesTanStack
              responsiblePartyId={organizationId}
              queryParams={queryParams}
              activeCreditFacilities={creditFacilities}
              optionalDescription={
                <>
                  <Text fontSize="l" width="80%" mb="35px">
                    Finley has extracted the key deliverables and deadlines from
                    your credit agreement.
                  </Text>
                </>
              }
            />
          ) : (
            <DeliverableContainers
              creditFacilityIds={creditFacilities.map((cf) => cf.id)}
            />
          )
        }
      />
      <Route
        path="/:deliverableId"
        element={
          !showV4Deliverables ? (
            <DeliverableDetailRoute />
          ) : (
            <DeliverableContainerDetailView
              organizationId={organizationId}
              orgSlug={orgSlug}
              userId={userId}
            />
          )
        }
      />
      {showV4Deliverables && (
        <Route
          path="/:deliverableId/final-output-report-versions/:receiptId"
          element={<DeliverableContainerReceipts />}
        />
      )}
      <Route path="/*" element={<Navigate to="/deliverables" />} />
    </Routes>
  );
};
